import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import ViewControlBar from "./ViewControlBar.jsx";
import {
  ORIGIN,
  applyCameraView,
  centerObjectAtOrigin,
  configurePreviewRenderer,
  createPreviewLights,
  fitDistanceForObject,
} from "../lib/threeFraming.js";

export default function GlbViewer({ src, className = "" }) {
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const activeViewRef = useRef("iso");
  const [loading, setLoading] = useState(Boolean(src));
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState("iso");

  const handleView = useCallback((preset) => {
    activeViewRef.current = preset;
    setActiveView(preset);
    apiRef.current?.setView(preset);
  }, []);

  const handleFit = useCallback(() => {
    activeViewRef.current = "iso";
    setActiveView("iso");
    apiRef.current?.setView("iso");
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !src) return undefined;

    setLoading(true);
    setError(null);
    activeViewRef.current = "iso";
    setActiveView("iso");

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0f);

    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 1_000_000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    configurePreviewRenderer(renderer);
    container.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.screenSpacePanning = false;
    controls.target.copy(ORIGIN);

    createPreviewLights(scene);

    let model = null;
    let fitDistance = 100;
    let animId = null;
    let cancelled = false;

    const frameModel = (preset = "iso") => {
      if (!model) return;
      fitDistance = fitDistanceForObject(model, camera);
      controls.minDistance = fitDistance * 0.1;
      controls.maxDistance = fitDistance * 10;
      applyCameraView(camera, controls, model, preset, fitDistance);
    };

    apiRef.current = {
      setView: (preset) => frameModel(preset),
    };

    const resize = () => {
      const width = Math.max(container.clientWidth, 1);
      const height = Math.max(container.clientHeight, 1);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
      if (model) frameModel(activeViewRef.current);
    };

    const loader = new GLTFLoader();
    loader.load(
      src,
      (gltf) => {
        if (cancelled) return;

        model = gltf.scene;
        model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = false;
            child.receiveShadow = false;
            if (child.material) {
              child.material.side = THREE.DoubleSide;
              child.material.needsUpdate = true;
            }
          }
        });

        centerObjectAtOrigin(model);
        scene.add(model);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (cancelled) return;
            resize();
            frameModel("iso");
            setLoading(false);
          });
        });
      },
      undefined,
      (err) => {
        if (cancelled) return;
        setLoading(false);
        setError(err?.message || "Could not load GLB preview.");
      }
    );

    const animate = () => {
      animId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };

    resize();
    animate();

    const observer = new ResizeObserver(resize);
    observer.observe(container);

    return () => {
      cancelled = true;
      apiRef.current = null;
      cancelAnimationFrame(animId);
      observer.disconnect();
      controls.dispose();
      if (model) {
        model.traverse((child) => {
          if (child.isMesh) {
            child.geometry?.dispose();
            if (Array.isArray(child.material)) {
              child.material.forEach((material) => material.dispose());
            } else {
              child.material?.dispose();
            }
          }
        });
      }
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [src]);

  if (!src) {
    return (
      <div
        className={`flex h-[420px] items-center justify-center rounded-xl border border-dashed border-white/15 bg-white/[0.02] text-sm text-white/45 ${className}`}
      >
        Your generated model preview will appear here.
      </div>
    );
  }

  return (
    <div
      className={`relative h-[420px] w-full overflow-hidden rounded-xl border border-white/10 bg-black/30 ${className}`}
      style={{
        background:
          "radial-gradient(circle at 50% 35%, rgba(56,189,248,0.08), transparent 55%)",
      }}
    >
      <div ref={containerRef} className="absolute inset-0 h-full w-full" />
      {!loading && !error && (
        <ViewControlBar activeView={activeView} onSelect={handleView} onFit={handleFit} />
      )}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/20 text-sm text-white/55">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading preview…
        </div>
      )}
      {error && (
        <div className="absolute inset-x-4 bottom-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}
    </div>
  );
}
